/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  feedbackService,
  type GetFeedbackParams,
} from "@src/@api-services/feedback";
import type { IFeedback } from "../types";

interface FeedbackState {
  data: IFeedback[];
  total: number;
  params: GetFeedbackParams;
  selectedFeedback: IFeedback | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

export const getFeedbacks = createAsyncThunk(
  "feedback/getFeedbacks",
  async (params: GetFeedbackParams, { rejectWithValue }) => {
    try {
      const response = await feedbackService.getFeedbacks(params);
      return {
        params,
        data: (response.data.data.data || []) as IFeedback[],
        total: response.data.data.totalRecord || 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch feedback"
      );
    }
  }
);

export const getFeedback = createAsyncThunk(
  "feedback/getFeedback",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await feedbackService.getFeedback(id);
      return response.data.data as IFeedback;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch feedback details"
      );
    }
  }
);

const initialState: FeedbackState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedFeedback: null,
  loading: false,
  detailLoading: false,
  error: null,
};

export const feedbackSlice = createSlice({
  name: "feedback",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedFeedback: (state) => {
      state.selectedFeedback = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeedbacks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getFeedback.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getFeedback.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedFeedback = action.payload;
      })
      .addCase(getFeedback.rejected, (state) => {
        state.detailLoading = false;
        state.selectedFeedback = null;
      });
  },
});

export const { clearError, clearSelectedFeedback } = feedbackSlice.actions;
export default feedbackSlice.reducer;
